import { useEffect, useState } from 'react';
import { useForm, UseFormReturnType } from '@mantine/form';
import { FieldWithValue, FormValuesType } from 'src/UI/dynamicUI/types';
import { useSearchParams, useParams } from 'react-router';
import {
  FormAccordion,
  FormAccordionItem,
  FormAccordionItemList,
  ReadOnlyWrapper,
} from 'src/UI/dynamicUI/layout/DynamicEntitySection';
import { Box, Grid } from '@mantine/core';
import styles from 'src/financial/financial-data/financial-item/forms/common/FormLayout.module.css';
import {
  getFinancialFormSetupValues,
  ProductFormComponentProps,
  updateFeedInfoAfterAssetCreation,
} from 'src/financial/financial-data/financial-item/forms/common/FinancialFormHelpers';
import { useDynamicUI } from 'src/UI/dynamicUI/DynamicUiContext';
import { DynamicUiProviderWrapper } from 'src/financial/financial-data/financial-item/forms/common/DynamicUiProviderWrapper';
import { FinancialFormField } from 'src/financial/financial-data/financial-item/forms/common/FinancialFormField';
import { GridPair } from 'src/financial/financial-data/financial-item/forms/common/GridPair';
import {
  BENEFICIARY,
  BeneficiaryFormAccordionItemList,
} from 'src/financial/financial-data/financial-item/forms/common/BeneficiaryFormAccordionItemList';
import { ContributionFormAccordionItemList } from 'src/financial/financial-data/financial-item/forms/common/ContributionFormAccordionItemList';
import {
  FeedInfo,
  FinancialFormContainer,
} from 'src/financial/financial-data/financial-item/forms/common/FinancialFormContainer';
import {
  FormTitleWithConnectedDataBadge,
  useConnectedDataIcon,
} from 'src/financial/financial-data/financial-item/forms/common/ConnectedDataBadge';
import {
  useValidateProviderReference,
  validateProviderReferenceAndSetError,
} from 'src/financial/financial-data/hooks/useValidateProviderReference';
import { scrollToElementWithError, validateContributionEndDate } from './common/validationUtils';

const DEFINED_BENEFIT_PENSION_PRODUCT = 'DefinedBenefitPension';
const DEFINED_BENEFIT_PENSION_BREAKDOWN_ITEM = 'DefinedBenefitPensionBreakdownItem';
const CONTRIBUTION = 'DbPensionContribution';
export function DefinedBenefitPension(props: ProductFormComponentProps) {
  const { onSave } = props;
  const [searchParams] = useSearchParams();
  const { clientId = '' } = useParams<{ clientId: string }>();
  const { validateProviderReference } = useValidateProviderReference(clientId);

  const {
    isCreateMode,
    uiData,
    initialValues,
    onDeleteConfirm,
    feedInfo: initialFeedInfo,
    onConnectAsset,
  } = getFinancialFormSetupValues(DEFINED_BENEFIT_PENSION_PRODUCT, props, [
    { product: DEFINED_BENEFIT_PENSION_BREAKDOWN_ITEM, field: 'breakdown_items' },
    { product: CONTRIBUTION, field: 'contributions' },
    { product: BENEFICIARY, field: 'beneficiaries' },
  ]);

  const [feedInfo, setFeedInfo] = useState<FeedInfo>(initialFeedInfo);

  const form = useForm<FormValuesType>({
    mode: 'uncontrolled',
    initialValues: initialValues,
    validate: {
      [CONTRIBUTION]: {
        end_date: (value, values, path) => {
          return validateContributionEndDate(CONTRIBUTION, value, values, path);
        },
      },
    },
  });

  useEffect(() => {
    const providerReference = searchParams.get('providerReference');
    if (providerReference && isCreateMode) {
      form.setFieldValue(
        `${DEFINED_BENEFIT_PENSION_PRODUCT}.provider_reference_number`,
        providerReference,
      );
    }
  }, [searchParams, isCreateMode, form]);

  const handleSave = async (values: FormValuesType) => {
    if (
      DEFINED_BENEFIT_PENSION_BREAKDOWN_ITEM in values &&
      Array.isArray(values[DEFINED_BENEFIT_PENSION_BREAKDOWN_ITEM]) &&
      CONTRIBUTION in values &&
      Array.isArray(values[CONTRIBUTION]) &&
      BENEFICIARY in values &&
      Array.isArray(values[BENEFICIARY])
    ) {
      const pensionData = values[DEFINED_BENEFIT_PENSION_PRODUCT] as Record<string, unknown>;

      const isProviderReferenceValid = await validateProviderReferenceAndSetError(
        pensionData,
        clientId,
        form,
        DEFINED_BENEFIT_PENSION_PRODUCT,
        validateProviderReference,
      );
      if (!isProviderReferenceValid) {
        return;
      }

      const mappedValues = {
        ...pensionData,
        breakdown_items: values[DEFINED_BENEFIT_PENSION_BREAKDOWN_ITEM].map(item => ({ ...item })),
        contributions: values[CONTRIBUTION].map(item => ({ ...item })),
        beneficiaries: values[BENEFICIARY].map(item => ({ ...item })),
      };

      await onSave(mappedValues, uiData.ui.entitiesMetadata[DEFINED_BENEFIT_PENSION_PRODUCT]);

      updateFeedInfoAfterAssetCreation(
        form,
        DEFINED_BENEFIT_PENSION_PRODUCT,
        isCreateMode,
        setFeedInfo,
      );
    }
  };

  const handleAddBreakdownItem = () => {
    form.insertListItem(DEFINED_BENEFIT_PENSION_BREAKDOWN_ITEM, { type: null, amount: null });
  };

  const untypedItems = form.getValues()[DEFINED_BENEFIT_PENSION_BREAKDOWN_ITEM];
  const breakdownItems = Array.isArray(untypedItems) ? untypedItems : [];

  return (
    <DynamicUiProviderWrapper
      form={form}
      uiData={uiData}
      onDeleteConfirm={isCreateMode ? () => {} : onDeleteConfirm}
      isCreateMode={isCreateMode}
    >
      <form onSubmit={form.onSubmit(handleSave, scrollToElementWithError)} autoComplete={'off'}>
        <FormContent
          breakdownItems={breakdownItems}
          handleAddBreakdownItemPress={handleAddBreakdownItem}
          form={form}
          feedInfo={feedInfo}
          onConnect={onConnectAsset}
        />
      </form>
    </DynamicUiProviderWrapper>
  );
}

type FormContentProps = {
  breakdownItems: FieldWithValue[];
  handleAddBreakdownItemPress: () => void;
  form: UseFormReturnType<FormValuesType>;
  feedInfo: FeedInfo;
  onConnect?: (id: string) => void;
};

const FormContent = ({
  breakdownItems,
  handleAddBreakdownItemPress,
  form,
  feedInfo,
  onConnect,
}: FormContentProps) => {
  const { isEditing } = useDynamicUI();
  const connectedDataIcon = useConnectedDataIcon(feedInfo.isUpdatedAutomatically);

  const summaryContent = (
    <>
      <Grid>
        <GridPair
          first={<DefinedBenefitPensionField field="type" />}
          second={<DefinedBenefitPensionField field="description" />}
        />
        <GridPair
          isHighlighted
          first={<DefinedBenefitPensionField field="scheme_name" />}
          second={<DefinedBenefitPensionField field="provider_reference_number" />}
        />

        <GridPair
          first={<DefinedBenefitPensionField field="scheme_join_date" />}
          second={<DefinedBenefitPensionField field="leaving_date" />}
        />
        <GridPair
          first={<DefinedBenefitPensionField field="retirement_date" />}
          second={<DefinedBenefitPensionField field="status" forceEditable />}
        />
        <GridPair
          isHighlighted
          first={<DefinedBenefitPensionField field="value" />}
          second={<DefinedBenefitPensionField field="value_date" />}
        />
        <GridPair
          first={<DefinedBenefitPensionField field="is_serviced_by_advisor" forceEditable />}
        />
        <GridPair fullWidth first={<DefinedBenefitPensionField field="notes" />} />
      </Grid>
    </>
  );

  const titleText = 'Assets > Pensions > Defined benefit';
  const titleWithBadge = (
    <FormTitleWithConnectedDataBadge
      title={titleText}
      showBadge={feedInfo.isUpdatedAutomatically}
    />
  );

  return (
    <FinancialFormContainer feedInfo={feedInfo} onConnect={onConnect} title={titleWithBadge}>
      <FormAccordion
        defaultValue={[
          'summary',
          'total',
          'breakdown',
          'protected',
          'contribution',
          'beneficiaries',
        ]}
      >
        <FormAccordionItem value="summary" label="Summary" titleIcon={connectedDataIcon}>
          {feedInfo.isUpdatedAutomatically ? (
            <ReadOnlyWrapper>{summaryContent}</ReadOnlyWrapper>
          ) : (
            summaryContent
          )}
        </FormAccordionItem>

        <FormAccordionItem value="total" label="Total pension (per annum for amounts and rates)">
          <Grid>
            <GridPair
              isHighlighted
              first={<DefinedBenefitPensionField field="amount" />}
              second={<DefinedBenefitPensionField field="amount_applicable_date" />}
            />
            <GridPair
              first={<DefinedBenefitPensionField field="dependant_fraction_percentage" />}
            />
          </Grid>
          <Grid>
            <GridPair
              isHighlighted
              first={<DefinedBenefitPensionField field="increase_in_deferment_percentage" />}
              second={<DefinedBenefitPensionField field="increase_in_deferment_percentage_cap" />}
            />
            <GridPair
              isHighlighted
              first={<DefinedBenefitPensionField field="increase_in_payment_percentage" />}
              second={<DefinedBenefitPensionField field="increase_in_payment_percentage_cap" />}
            />
          </Grid>
        </FormAccordionItem>

        <FormAccordionItemList
          value="breakdown"
          label="Pension breakdown (per annum for amounts and rates)"
          counterLabel="Breakdown"
          button={{
            label: 'Add breakdown item',
            onClick: handleAddBreakdownItemPress,
          }}
          isEditing={isEditing}
          form={form}
          entityType={DEFINED_BENEFIT_PENSION_BREAKDOWN_ITEM}
        >
          {breakdownItems.map((_, index) => {
            return (
              <Box key={index} className={styles.breakdownItem}>
                <Grid>
                  <GridPair
                    first={<DefinedBenefitPensionBreakdownItemField field="type" index={index} />}
                  />
                  <GridPair
                    isHighlighted
                    first={<DefinedBenefitPensionBreakdownItemField field="amount" index={index} />}
                    second={
                      <DefinedBenefitPensionBreakdownItemField
                        field="applicable_date"
                        index={index}
                      />
                    }
                  />
                </Grid>
                <Grid>
                  <GridPair
                    isHighlighted
                    first={
                      <DefinedBenefitPensionBreakdownItemField
                        field="increase_in_deferment_percentage"
                        index={index}
                      />
                    }
                    second={
                      <DefinedBenefitPensionBreakdownItemField
                        field="increase_in_deferment_percentage_cap"
                        index={index}
                      />
                    }
                  />
                  <GridPair
                    isHighlighted
                    first={
                      <DefinedBenefitPensionBreakdownItemField
                        field="increase_in_payment_percentage"
                        index={index}
                      />
                    }
                    second={
                      <DefinedBenefitPensionBreakdownItemField
                        field="increase_in_payment_percentage_cap"
                        index={index}
                      />
                    }
                  />
                  <GridPair
                    first={
                      <DefinedBenefitPensionBreakdownItemField
                        field="dependant_fraction_percentage"
                        index={index}
                      />
                    }
                  />
                </Grid>
              </Box>
            );
          })}
        </FormAccordionItemList>

        <ContributionFormAccordionItemList
          form={form}
          isEditing={isEditing}
          contributionType={CONTRIBUTION}
        />

        <FormAccordionItem value="protected" label="Protected tax free cash (TFC)">
          <Grid>
            <GridPair
              first={<DefinedBenefitPensionField field="does_protected_tfc_apply" />}
              second={<DefinedBenefitPensionField field="protected_tfc_amount" />}
            />
            <GridPair
              first={<DefinedBenefitPensionField field="protected_tfc_percentage" />}
              second={<DefinedBenefitPensionField field="protected_tfc_pension_age" />}
            />
            <GridPair
              first={<DefinedBenefitPensionField field="is_protected_tfc_transferable" />}
              second={<DefinedBenefitPensionField field="is_loss_of_transfer_warning_given" />}
            />
            <GridPair first={<DefinedBenefitPensionField field="is_reserved_post_transfer" />} />
          </Grid>
        </FormAccordionItem>
        <BeneficiaryFormAccordionItemList form={form} isEditing={isEditing} />
      </FormAccordion>
    </FinancialFormContainer>
  );
};

type DefinedBenefitPensionFieldProps = {
  field: string;
  forceEditable?: boolean;
};

const DefinedBenefitPensionField = ({ field, forceEditable }: DefinedBenefitPensionFieldProps) => (
  <FinancialFormField
    field={field}
    product={DEFINED_BENEFIT_PENSION_PRODUCT}
    forceEditable={forceEditable}
  />
);

type DefinedBenefitPensionBreakdownItemFieldProps = {
  field: string;
  index: number;
};

const DefinedBenefitPensionBreakdownItemField = ({
  field,
  index,
}: DefinedBenefitPensionBreakdownItemFieldProps) => (
  <FinancialFormField
    field={field}
    product={DEFINED_BENEFIT_PENSION_BREAKDOWN_ITEM}
    index={index}
  />
);
